import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  ArrowRight,
  PackagePlus,
  ScanLine,
  ShoppingCart,
} from 'lucide-react';
import { FaFileAlt, FaHome, FaTruck } from 'react-icons/fa';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import './FooterNav.css';
import { scrollToTop } from '../utils/scroll';
const FOOTER_TABS = [
  { id: 'dashboard', label: 'Home', icon: FaHome },
  { id: 'inventory', label: 'Inventory', icon: PackagePlus },
  { id: 'scanBill', label: 'Scan Bill', icon: ScanLine, isCta: true },
  { id: 'reports', label: 'Reports', icon: FaFileAlt },
  { id: 'vendors', label: 'Reorder', icon: FaTruck },
];
const SCAN_OPTIONS = [
  {
    id: 'stock-in',
    title: 'Stock In Bill',
    icon: PackagePlus,
    target: 'stock-in-scan',
    accent: '#f97316',
  },
  {
    id: 'sales',
    title: "Today's Sales Bill",
    icon: ShoppingCart,
    target: 'todays-sales-report',
    accent: '#22c55e',
  },
];

// Scan Bill Modal Component
const ScanBillModal = ({ isOpen, onClose, onSelect }) => {
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const handleSelect = useCallback(
    (id) => {
      setSelectedId(id);
      window.setTimeout(() => {
        onSelect(id);
        setSelectedId(null);
      }, 350);
    },
    [onSelect],
  );

  const handleClose = useCallback(() => {
    setSelectedId(null);
    setHoveredId(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [handleClose, isOpen]);
  const modalContent = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.14 }}
        >
          <motion.div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(17, 24, 39, 0.3)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
            }}
            onClick={handleClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.14 }}
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="footer-scan-title"
            style={{
              position: 'relative',
              width: 'min(500px, calc(100% - 20px))',
              maxWidth: '500px',
              boxSizing: 'border-box',
              background: '#ffffff',
              borderRadius: '0.5rem',
              padding: '28px 24px',
              zIndex: 10,
              boxShadow: '0 25px 60px -15px rgba(17, 24, 39, 0.12)',
            }}
            initial={{ opacity: 0, y: 28, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{
              type: 'tween',
              ease: [0.22, 1, 0.36, 1],
              duration: 0.18,
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <h2
                id="footer-scan-title"
                style={{
                  margin: 0,
                  fontSize: 24,
                  fontWeight: 700,
                  color: '#000000',
                  lineHeight: 1.2,
                }}
              >
                Scan Bill
              </h2>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {SCAN_OPTIONS.map((option, index) => {
                const OptionIcon = option.icon;
                const isHovered = hoveredId === option.id;
                const isSelected = selectedId === option.id;
                const isHighlighted = isSelected;

                return (
                  <motion.button
                    key={option.id}
                    type="button"
                    style={{
                      width: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: '0.5rem',
                      border: '1.5px solid',
                      borderColor: isHighlighted ? option.accent : '#e5e7eb',
                      background: '#ffffff',
                      boxShadow: isHighlighted ? `0 0 0 1px ${option.accent}33` : 'none',
                      textAlign: 'left',
                      padding: 0,
                      cursor: 'pointer',
                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                    }}
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.97 }}
                    onHoverStart={() => setHoveredId(option.id)}
                    onHoverEnd={() => setHoveredId(null)}
                    onClick={() => handleSelect(option.id)}
                  >
                    <div
                      style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 20,
                        padding: '1rem',
                      }}
                    >
                      <motion.div
                        style={{
                          flexShrink: 0,
                          width: 56,
                          height: 56,
                          borderRadius: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        animate={{
                          scale: isSelected ? 0.96 : isHovered ? 1.02 : 1,
                          rotate: isSelected ? -2 : 0,
                        }}
                        transition={{ type: 'spring', damping: 18, stiffness: 220 }}
                      >
                        <motion.div
                          animate={{ scale: isSelected ? 1.08 : 1 }}
                          transition={{ type: 'spring', damping: 14, stiffness: 220 }}
                        >
                          <OptionIcon size={26} strokeWidth={1.8} style={{ color: option.accent }} />
                        </motion.div>
                      </motion.div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{ margin: 0, fontWeight: 600, color: '#000000', fontSize: 18 }}>{option.title}</h3>
                      </div>

                      <motion.div
                        style={{ flexShrink: 0 }}
                        animate={{ x: isHovered ? 2 : 0, scale: isSelected ? 1.08 : 1 }}
                        transition={{ type: 'spring', damping: 18, stiffness: 220 }}
                      >
                        <ArrowRight
                          size={20}
                          strokeWidth={2}
                          style={{
                            color: isHovered || isSelected ? option.accent : '#6b7280',
                            transition: 'color 0.2s',
                          }}
                        />
                      </motion.div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default function FooterNav({ current, goTo, isLocked = false, tsrLockActive = false, allowedScreens = [] }) {
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth <= 768;
  });

  useEffect(() => {
    const onResize = () => {
      setIsMobileViewport(window.innerWidth <= 768);
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Check if user is QR billing user
  // Fetch QR billing status from API instead of localStorage
  const [isQRUser, setIsQRUser] = React.useState(false);

  React.useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.warn('FooterNav: No token found');
          return;
        }

        const apiUrl = process.env.REACT_APP_API_URL || 'https://be.invexis.co.in/api';

        const response = await fetch(`${apiUrl}/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          const qrBilling = data.isQRBilling || data.business?.isQRBilling || false;
          setIsQRUser(qrBilling);
        } else {
          console.error('FooterNav: User API failed with status:', response.status);
          setIsQRUser(false);
        }
      } catch (error) {
        console.error('FooterNav: Failed to fetch user QR billing status:', error);
        setIsQRUser(false);
      }
    };

    fetchUserData();
  }, []);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getTabGroups = () => ({
    dashboard: isQRUser
      ? ['dashboard', 'overview', 'qr-owner-dashboard', 'qr-menu-management', 'qr-analytics']
      : ['dashboard', 'overview'],
    inventory: [
      'inventory',
      'inventory-overview',
      'inventory-stock-in',
      'inventory-stock-out',
      'stock-in',
      'stock-in-scan',
      'stock-in-manual',
      'stock-out',
      'stockout',
      'usage',
      'minimal-stock',
    ],
    scanBill: ['stock-in-scan', 'todays-sales-report'],
    reports: ['reports', 'sales-report', 'todays-sales-report'],
    vendors: ['vendors', 'create-reorder'],
  });

  // Treat related screens as part of a primary tab so the correct item is highlighted
  const isActive = (id) => {
    const mappingScreens = new Set(['itemmap', 'map', 'map1', 'map2', 'map3', 'qr-itemmap']);
    if (mappingScreens.has(current)) {
      return false;
    }

    if (id === 'scanBill') {
      return false;
    }

    const groups = getTabGroups();
    const list = groups[id] || [id];
    if (list.includes(current)) return true;

    // Normalize inventory route variants like inventory-stock-out / inventory-stock-in
    if (id === 'inventory' && typeof current === 'string' && current.startsWith('inventory-')) {
      return true;
    }

    return false;
  };

  const isTabDisabled = (tabId) => {
    if (isLocked) {
      return true;
    }

    const groups = getTabGroups();
    const candidateScreens = groups[tabId] || [tabId];
    return tsrLockActive && !candidateScreens.some((screen) => allowedScreens.includes(screen));
  };

  const handleTabClick = (tab) => {
    if (isTabDisabled(tab.id)) return;

    // Reset scroll even if the user taps the active tab again.
    scrollToTop();

    if (tab.id === 'scanBill') {
      setIsModalOpen(true);
      return;
    }

    const targetByTab = {
      dashboard: 'dashboard',
      inventory: 'inventory',
      reports: 'reports',
      vendors: 'create-reorder',
    };

    goTo(targetByTab[tab.id] || tab.id);
  };

  // Show banner only for TSR recovery mode.
  // During onboarding lock we keep footer disabled but do not show any lock text.
  const showLockBanner = tsrLockActive;
  const shellModeClass = isMobileViewport ? 'is-mobile' : 'is-desktop';

  return (
    <>
      <ScanBillModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelect={(selectedType) => {
          const selectedOption = SCAN_OPTIONS.find((option) => option.id === selectedType);
          if (selectedOption) {
            goTo(selectedOption.target);
          }
          setIsModalOpen(false);
        }}
      />

      <div className={`footer-nav-shell ${showLockBanner ? 'locked' : ''} ${shellModeClass}`}>
        {showLockBanner && (
          <div className="footer-lock-banner">{tsrLockActive ? 'TSR Recovery Mode' : 'Navigation Locked'}</div>
        )}

        <div className="footer-nav-inner">
          <LayoutGroup id="core-footer-nav-layout">
            {FOOTER_TABS.map((tab) => {
              const Icon = tab.icon;
              const active = isActive(tab.id);
              const disabled = isTabDisabled(tab.id);

              if (tab.isCta) {
                return (
                  <button
                    key={tab.id}
                    type="button"
                    className={`footer-nav-cta ${disabled ? 'is-disabled' : ''}`}
                    onClick={() => handleTabClick(tab)}
                    aria-label={tab.label}
                    disabled={disabled}
                  >
                    <motion.span
                      className="footer-nav-cta-button"
                      whileTap={isMobileViewport ? { scale: 0.82, rotate: -8 } : { scale: 0.94 }}
                      whileHover={isMobileViewport ? { scale: 1.02 } : undefined}
                      transition={
                        isMobileViewport
                          ? { type: 'spring', damping: 16, stiffness: 260 }
                          : { duration: 0.15 }
                      }
                    >
                      <Icon className="footer-nav-icon footer-nav-scan-icon" size={24} strokeWidth={2.2} />
                    </motion.span>
                    <span className="footer-nav-cta-label">{tab.label}</span>
                  </button>
                );
              }

              return (
                <button
                  key={tab.id}
                  type="button"
                  className={`footer-nav-item ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
                  onClick={() => handleTabClick(tab)}
                  aria-label={tab.label}
                  disabled={disabled}
                >
                  <span className="footer-nav-icon-wrap">
                    <Icon className="footer-nav-icon" size={20} strokeWidth={2} />
                    {active && (
                      <motion.div
                        layoutId="footer-nav-dot"
                        className="footer-nav-dot"
                        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                        aria-hidden="true"
                      />
                    )}
                  </span>
                  <span className="footer-nav-label">{tab.label}</span>
                </button>
              );
            })}
          </LayoutGroup>
        </div>

        <div className="footer-nav-safe-spacer" />
      </div>
    </>
  );
}
